/**
 * FloatingPreviewProvider — the app-level multitask window layer (2026-08-02 Miles
 * directive: a preview can become a fully functional draggable/resizable window that
 * stays with the user on ANY page). Mounted once at the App root, so windows survive
 * route changes; every preview shell offers a "Float" control that hands its payload
 * here. Same-day amendment (drone-sim report): the layer also owns an app-level modal
 * previewer + a document-level link interceptor, so clicking a platform-hosted file's
 * link anywhere opens the in-platform previewer instead of navigating away.
 *
 * It only draws: the windows, their order, the modal previewer's file, the link interceptor and the context
 * every shell reads (useFloatingPreview) are src/hooks/filePreview/floatingPreviews.ts (PV5-8:C part 1,
 * 2026-10-13).
 *
 * Which design draws them (the file preview part 2, PV5-8:C, 2026-10-13): where the `file-preview` surface shows
 * (config/site-design.json), the new design's viewer and windows, through withPavilion.tsx's doors (their own chunk,
 * `pavilion-file-preview`), with today's as the fallback when that chunk cannot be had; elsewhere today's. Both draw
 * from the same hooks, so they open, float, order and close the same way.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import FloatingPreviewWindow from './FloatingPreviewWindow';
import FilePreviewOverlay from './FilePreviewOverlay';
import { FloatingPreviewContext, useFloatingPreviews, type FloatingWindowState } from '../../hooks/filePreview/floatingPreviews';
import { designShows } from '../../design/previewPath';
import { PavilionFilePreviewDoor, PavilionFloatingPreviewDoor } from '../../design/pavilion/withPavilion';

export const FloatingPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const f = useFloatingPreviews();
  const newDesign = designShows('file-preview');

  const todaysWindow = (w: FloatingWindowState) => (
    <FloatingPreviewWindow
      key={w.id}
      state={w}
      onClose={() => f.close(w.id)}
      onFocus={() => f.focus(w.id)}
      onRect={(rect) => f.setRect(w.id, rect)}
    />
  );

  return (
    <FloatingPreviewContext.Provider value={f.api}>
      {children}
      {f.modal && (newDesign ? (
        <PavilionFilePreviewDoor
          file={f.modal.file}
          serialized={f.modal.serialized}
          onClose={f.closeModal}
          fallback={<FilePreviewOverlay file={f.modal.file} serialized={f.modal.serialized} onClose={f.closeModal} />}
        />
      ) : (
        <FilePreviewOverlay
          file={f.modal.file}
          serialized={f.modal.serialized}
          onClose={f.closeModal}
        />
      ))}
      {/* The new design's windows each reach <body> through the kit's portal, on the floating rung at their place in
          the windows' order; a fallback window is portaled there as today's are. */}
      {f.windows.length > 0 && (newDesign
        ? f.windows.map((w) => (
          <PavilionFloatingPreviewDoor
            key={w.id}
            state={w}
            onClose={() => f.close(w.id)}
            onFocus={() => f.focus(w.id)}
            onRect={(rect) => f.setRect(w.id, rect)}
            fallback={createPortal(todaysWindow(w), f.portalTarget)}
          />
        ))
        : createPortal(<>{f.windows.map(todaysWindow)}</>, f.portalTarget))}
    </FloatingPreviewContext.Provider>
  );
};

export default FloatingPreviewProvider;
